import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from "lucide-react";
import { clsx } from "clsx";
import {
  useAnnouncements,
  useMarkAnnouncementRead,
} from "../hooks/useWhatsNew";
import { AnnouncementBody } from "./AnnouncementBody";
import { sortAnnouncements } from "../types";
import type { Announcement } from "../types";

const MAX_ITEMS = 4;
/** Collapsed body height in px (Tailwind `max-h-24` = 6rem). Kept in sync with
 *  the class below so we can detect whether the content actually overflows. */
const COLLAPSED_PX = 96;

function formatDate(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Dashboard „Komunikaty" widget — latest published announcements the user may see.
 *  Contractors get only announcements targeting them (scoped on the backend). */
export function WhatsNewWidget() {
  const { data: announcements = [], isLoading } = useAnnouncements();

  // Dashboard shows only UNREAD published announcements; once read they drop off
  // the main page and live on only in the „Co nowego" tab.
  const unread: Announcement[] = sortAnnouncements(
    announcements.filter((a) => a.status === "published" && !a.is_read),
  );
  if (isLoading || unread.length === 0) return null;

  const items = unread.slice(0, MAX_ITEMS);

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-1.5">
          <Sparkles size={13} /> Komunikaty
        </p>
        <Link
          to="/whats-new"
          className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] inline-flex items-center gap-1"
        >
          Zobacz wszystkie <ArrowUpRight size={13} />
        </Link>
      </div>

      <div className="space-y-3">
        {items.map((a) => (
          <WhatsNewItem key={a.id} announcement={a} />
        ))}
      </div>
    </div>
  );
}

/** Single announcement card. Long bodies collapse to {@link COLLAPSED_PX} and
 *  expose a „Rozwiń"/„Zwiń" toggle; the toggle only shows when the content
 *  actually overflows, so short announcements stay clean. */
function WhatsNewItem({ announcement: a }: { announcement: Announcement }) {
  // Mutation lives here (not in the parent) so pending/error state is per card.
  const markRead = useMarkAnnouncementRead();
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const unread = !a.is_read;
  // The dashboard only lists unread items, so an important one always pulses here.
  const pulsing = a.is_important && unread;

  // Detect overflow from the body's natural height (unaffected by the clamp on
  // the wrapper). Re-check on resize so wrapping changes keep the toggle honest.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollHeight > COLLAPSED_PX + 4);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [a.body]);

  return (
    <div
      className={clsx(
        "border p-4 bg-[var(--color-bg-surface)]",
        a.is_important
          ? "border-red-400 dark:border-red-500"
          : unread
            ? "border-[var(--color-primary)]"
            : "border-[var(--color-border)]",
        pulsing && "animate-important-pulse",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2 min-w-0">
          {unread && (
            <span className="w-2 h-2 rounded-full bg-[var(--color-primary)] shrink-0" />
          )}
          <span className="truncate">{a.title}</span>
          {a.is_important && (
            <span
              className={clsx(
                "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 shrink-0",
                "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
                pulsing && "animate-pulse",
              )}
            >
              <AlertTriangle size={11} /> Ważne
            </span>
          )}
        </h3>
        <span className="text-[11px] text-[var(--text-muted)] shrink-0">
          {formatDate(a.published_at)}
        </span>
      </div>
      {a.body && (
        <div
          className={clsx(
            "relative mt-2 overflow-hidden",
            !expanded && "max-h-24",
          )}
        >
          <div ref={contentRef}>
            <AnnouncementBody text={a.body} />
          </div>
          {!expanded && overflowing && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-[var(--color-bg-surface)] to-transparent" />
          )}
        </div>
      )}
      {/* Actions share one flex row so the toggle and „Przeczytane" never collide. */}
      {(overflowing || unread) && (
        <div className="mt-2.5 flex items-center gap-4">
          {overflowing && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
            >
              {expanded ? (
                <>
                  <ChevronUp size={12} /> Zwiń
                </>
              ) : (
                <>
                  <ChevronDown size={12} /> Rozwiń
                </>
              )}
            </button>
          )}
          {unread && (
            <button
              onClick={() => markRead.mutate(a.id)}
              disabled={markRead.isPending}
              className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border border-[var(--color-border)] text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-50 disabled:cursor-wait"
            >
              <Check size={12} />
              {markRead.isPending ? "Zapisywanie…" : "Przeczytane"}
            </button>
          )}
        </div>
      )}
      {markRead.isError && (
        <p className="mt-1.5 text-[11px] text-red-500">
          Nie udało się oznaczyć jako przeczytane. Spróbuj ponownie.
        </p>
      )}
    </div>
  );
}
